import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { Theme } from "../../lib/theme.js";
import { applyTheme, getInitialTheme } from "../../lib/theme.js";

export type PageId =
  | "overview"
  | "costs"
  | "alarms"
  | "metrics"
  | "uptime"
  | "deployments"
  | "pipelines"
  | "budgets";

interface ShellProps {
  page: PageId;
  onNavigate: (page: PageId) => void;
  alarmCount: number;
  dataAsOf: string | null;
  onRefresh: () => void;
  onLogout?: () => void;
  title: string;
  subtitle: string;
  children: ReactNode;
}

interface IconProps {
  size?: number;
  strokeColor?: string;
}

function icon(paths: ReactNode) {
  return function Icon({ size = 17, strokeColor = "currentColor" }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={strokeColor}
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flex: "none" }}
        aria-hidden="true"
      >
        {paths}
      </svg>
    );
  };
}

const IconGrid = icon(
  <>
    <rect width="7" height="9" x="3" y="3" rx="1" />
    <rect width="7" height="5" x="14" y="3" rx="1" />
    <rect width="7" height="9" x="14" y="12" rx="1" />
    <rect width="7" height="5" x="3" y="16" rx="1" />
  </>,
);

const IconDollar = icon(
  <>
    <circle cx="12" cy="12" r="10" />
    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
    <path d="M12 18V6" />
  </>,
);

const IconBell = icon(
  <>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    <path d="M4 2C2.8 3.7 2 5.7 2 8" />
    <path d="M22 8c0-2.3-.8-4.3-2-6" />
  </>,
);

const IconPulse = icon(
  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />,
);

const IconTarget = icon(
  <>
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </>,
);

const IconClock = icon(
  <>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </>,
);

const IconGlobe = icon(
  <>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
    <path d="M2 12h20" />
  </>,
);

const IconRefresh = icon(
  <>
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
    <path d="M8 16H3v5" />
  </>,
);

const IconMoon = icon(<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />);

const IconSun = icon(
  <>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2" />
    <path d="M12 20v2" />
    <path d="m4.93 4.93 1.41 1.41" />
    <path d="m17.66 17.66 1.41 1.41" />
    <path d="M2 12h2" />
    <path d="M20 12h2" />
    <path d="m6.34 17.66-1.41 1.41" />
    <path d="m19.07 4.93-1.41 1.41" />
  </>,
);

const IconShield = icon(
  <>
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    <path d="m9 12 2 2 4-4" />
  </>,
);

const IconRocket = icon(
  <>
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
  </>,
);

const IconGauge = icon(
  <>
    <path d="m12 14 4-4" />
    <path d="M3.34 19a10 10 0 1 1 17.32 0" />
  </>,
);

const IconWorkflow = icon(
  <>
    <rect width="8" height="8" x="3" y="3" rx="2" />
    <path d="M7 11v4a2 2 0 0 0 2 2h4" />
    <rect width="8" height="8" x="13" y="13" rx="2" />
  </>,
);

interface NavDef {
  id: PageId;
  label: string;
  Icon: (props: IconProps) => ReactNode;
}

const NAV_ITEMS: NavDef[] = [
  { id: "overview", label: "Overview", Icon: IconGrid },
  { id: "costs", label: "Costs", Icon: IconDollar },
  { id: "alarms", label: "Alarms", Icon: IconBell },
  { id: "metrics", label: "Metrics", Icon: IconPulse },
  { id: "uptime", label: "Uptime", Icon: IconGauge },
  { id: "deployments", label: "Deployments", Icon: IconRocket },
  { id: "pipelines", label: "Pipelines", Icon: IconWorkflow },
  { id: "budgets", label: "Budgets", Icon: IconTarget },
];

export function Shell({
  page,
  onNavigate,
  alarmCount,
  dataAsOf,
  onRefresh,
  onLogout,
  title,
  subtitle,
  children,
}: ShellProps) {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return (
    <div className="shell">
      <aside className="shell__sidebar">
        <div className="shell__brand">
          <img src="/logo-mark.svg" alt="" style={{ width: 24, height: 24 }} />
          <span className="shell__brand-text">Azertica</span>
          <span className="shell__brand-badge">OPS</span>
        </div>
        <nav className="shell__nav">
          {NAV_ITEMS.map(({ id, label, Icon }) => {
            const active = page === id;
            const flagged = id === "alarms" && alarmCount > 0;
            return (
              <button
                key={id}
                type="button"
                title={label}
                onClick={() => onNavigate(id)}
                aria-current={active ? "page" : undefined}
                className={flagged ? "shell__nav-btn shell__nav-btn--flag" : "shell__nav-btn"}
              >
                <Icon />
                <span className="shell__nav-label">{label}</span>
                {flagged && <span className="shell__nav-count">{alarmCount}</span>}
              </button>
            );
          })}
        </nav>
        <div className="shell__foot">
          <div className="shell__readonly">
            <div className="shell__readonly-head">
              <IconShield size={14} strokeColor="var(--accent)" />
              Read-only
            </div>
            <div className="shell__readonly-body">
              ops-viewer role via Identity Center · 4 accounts · us-east-1
            </div>
          </div>
          <div className="shell__user">
            <span className="shell__avatar" title="Platform admin — ops@azertica.com">
              A
            </span>
            <span className="shell__user-info">
              <span className="shell__user-name">Platform admin</span>
              <span className="shell__user-mail">ops@azertica.com</span>
            </span>
            {onLogout && (
              <button
                type="button"
                title="Sign out"
                aria-label="Sign out"
                onClick={onLogout}
                className="shell__logout"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </aside>

      <div className="shell__body">
        <header className="shell__header">
          <div className="shell__header-title">
            <span
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: "-0.01em",
                color: "var(--fg)",
              }}
            >
              {title}
            </span>
            <span
              style={{
                fontSize: 11.5,
                color: "var(--fg-subtle)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {subtitle}
            </span>
          </div>
          <div className="shell__header-actions">
            <span className="hdr-pill hdr-asof" style={{ color: "var(--fg-subtle)" }}>
              <IconClock size={13} />
              {dataAsOf ? `Data as of ${dataAsOf}` : "Loading..."}
            </span>
            <button type="button" title="Refresh" aria-label="Refresh" className="hdr-btn" onClick={onRefresh}>
              <IconRefresh size={15} />
            </button>
            <span
              title="Primary region — N. Virginia"
              className="hdr-pill hdr-region"
              style={{ fontWeight: 600, color: "var(--fg-muted)" }}
            >
              <IconGlobe size={13} />
              us-east-1
            </span>
            <button
              type="button"
              title="Toggle theme"
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="hdr-btn"
              onClick={toggleTheme}
            >
              {theme === "dark" ? <IconSun size={15} /> : <IconMoon size={15} />}
            </button>
          </div>
        </header>

        <main className="shell__main">
          <div key={page} className="page-fade" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
